/**
 * Supabase User Profile Repository
 *
 * Implements IUserProfileRepository for Supabase storage.
 * Handles user profile CRUD and subscription management.
 *
 * @module repositories/supabase/SupabaseUserProfileRepository
 */
import { getSupabaseClient } from '../../services/auth/supabase';
import type { IUserProfileRepository } from '../interfaces/IUserProfileRepository';
import type { UserProfile, SubscriptionStatus, ProfileUpdateInput } from '../../types/user';
import type { Database } from '../../types/database.types';

type UserRow = Database['public']['Tables']['users']['Row'];
type UserInsert = Database['public']['Tables']['users']['Insert'];
type UserDbUpdate = Database['public']['Tables']['users']['Update'];

// =============================================================================
// Row Conversion Helpers
// =============================================================================

/**
 * Convert database row to UserProfile domain object.
 */
function rowToProfile(row: UserRow): UserProfile {
  return {
    id: row.id,
    email: row.email,
    username: row.display_name ?? row.email.split('@')[0],
    displayName: row.display_name ?? undefined,
    avatarUrl: row.avatar_url ?? undefined,
    createdAt: new Date(row.created_at).getTime(),
    subscriptionStatus: row.subscription_status as SubscriptionStatus,
    stripeCustomerId: row.stripe_customer_id ?? undefined,
    stripeSubscriptionId: row.stripe_subscription_id ?? undefined,
  };
}

/**
 * Convert UserProfile to database row for upsert.
 */
function profileToRow(profile: UserProfile): UserInsert {
  return {
    id: profile.id,
    email: profile.email,
    display_name: profile.displayName ?? profile.username ?? null,
    avatar_url: profile.avatarUrl ?? null,
    subscription_status: profile.subscriptionStatus,
    stripe_customer_id: profile.stripeCustomerId ?? null,
    stripe_subscription_id: profile.stripeSubscriptionId ?? null,
    onboarding_completed: null,
    onboarding_version: null,
  };
}

// =============================================================================
// Repository Implementation
// =============================================================================

export class SupabaseUserProfileRepository implements IUserProfileRepository {
  // ===========================================================================
  // Profile Operations
  // ===========================================================================

  async getProfile(userId: string): Promise<UserProfile | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch profile: ${error.message}`);
    }
    if (!data) return null;

    return rowToProfile(data);
  }

  async upsertProfile(profile: UserProfile): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const row = profileToRow(profile);

    const { error } = await supabase
      .from('users')
      .upsert(row, { onConflict: 'id' });

    if (error) throw new Error(`Failed to upsert profile: ${error.message}`);
  }

  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const updateData: UserDbUpdate = {};

    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.username !== undefined) updateData.display_name = updates.username;
    if (updates.displayName !== undefined) updateData.display_name = updates.displayName;
    if (updates.avatarUrl !== undefined) updateData.avatar_url = updates.avatarUrl;
    if (updates.subscriptionStatus !== undefined) updateData.subscription_status = updates.subscriptionStatus;
    if (updates.stripeCustomerId !== undefined) updateData.stripe_customer_id = updates.stripeCustomerId;
    if (updates.stripeSubscriptionId !== undefined) updateData.stripe_subscription_id = updates.stripeSubscriptionId;

    updateData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);

    if (error) throw new Error(`Failed to update profile: ${error.message}`);
  }

  async updateProfileFields(userId: string, fields: Partial<ProfileUpdateInput>): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const updateData: UserDbUpdate = {};

    // Only update allowed fields from ProfileUpdateInput
    if (fields.username !== undefined) updateData.display_name = fields.username;
    if (fields.displayName !== undefined) updateData.display_name = fields.displayName;
    if (fields.avatarUrl !== undefined) updateData.avatar_url = fields.avatarUrl;
    // Note: bio and timezone are not in the users table - they would need separate handling

    if (Object.keys(updateData).length === 0) return;

    updateData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);

    if (error) throw new Error(`Failed to update profile fields: ${error.message}`);
  }

  async insertProfileIfNotExists(profile: UserProfile): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const row = profileToRow(profile);

    // INSERT with ignoreDuplicates — never overwrites existing rows
    const { error } = await supabase
      .from('users')
      .upsert(row, { onConflict: 'id', ignoreDuplicates: true });

    if (error) throw new Error(`Failed to insert profile: ${error.message}`);
  }

  createDefaultProfile(userId: string, email: string): UserProfile {
    return {
      id: userId,
      email,
      username: email.split('@')[0],
      createdAt: Date.now(),
      subscriptionStatus: 'free',
    };
  }

  // ===========================================================================
  // Subscription Management
  // ===========================================================================

  async updateSubscription(
    userId: string,
    status: SubscriptionStatus,
    stripeCustomerId?: string,
    stripeSubscriptionId?: string
  ): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const updateData: UserDbUpdate = {
      subscription_status: status,
      updated_at: new Date().toISOString(),
    };

    if (stripeCustomerId !== undefined) {
      updateData.stripe_customer_id = stripeCustomerId;
    }
    if (stripeSubscriptionId !== undefined) {
      updateData.stripe_subscription_id = stripeSubscriptionId;
    }

    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);

    if (error) throw new Error(`Failed to update subscription: ${error.message}`);
  }

  // ===========================================================================
  // Username Operations
  // ===========================================================================

  async checkUsernameAvailable(username: string, excludeUserId?: string): Promise<boolean> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    let query = supabase
      .from('users')
      .select('id')
      .ilike('display_name', username);

    if (excludeUserId) {
      query = query.neq('id', excludeUserId);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to check username availability: ${error.message}`);

    return data.length === 0;
  }
}
